// @ts-nocheck
import * as THREE from "three";
import {
  getBlockDefinition,
  getDefaultBlockState
} from "../core/blockRegistry.ts";
import {
  MinecraftSpecialBlockRenderer
} from "./MinecraftSpecialBlockRenderer.ts";
import {
  MinecraftBlockEntityPreviewRenderer
} from "./MinecraftBlockEntityPreviewRenderer.ts";

const FACE_NAMES = ["down", "up", "north", "south", "west", "east"];

function stableHash(x, y, z, salt) {
  let value =
    Math.imul(x | 0, 73856093) ^
    Math.imul(y | 0, 19349663) ^
    Math.imul(z | 0, 83492791) ^
    Math.imul(salt | 0, 2654435761);
  value ^= value >>> 16;
  return value >>> 0;
}

function normalizeResourceLocation(resourceLocation, defaultNamespace) {
  const raw = String(resourceLocation || "");
  return raw.includes(":")
    ? raw
    : (defaultNamespace || "minecraft") + ":" + raw;
}

function matchesSimpleCondition(condition, state) {
  return Object.entries(condition || {}).every(function ([key, expected]) {
    if (key === "OR" || key === "AND") {
      return true;
    }

    const actual = String(state[key] ?? "");
    return String(expected)
      .split("|")
      .some(function (candidate) {
        return candidate === actual;
      });
  });
}

function matchesMultipartCondition(condition, state) {
  if (!condition) {
    return true;
  }

  if (Array.isArray(condition)) {
    return condition.every(function (entry) {
      return matchesMultipartCondition(entry, state);
    });
  }

  if (condition.OR) {
    const entries = Array.isArray(condition.OR)
      ? condition.OR
      : [condition.OR];
    return entries.some(function (entry) {
      return matchesMultipartCondition(entry, state);
    });
  }

  if (condition.AND) {
    const entries = Array.isArray(condition.AND)
      ? condition.AND
      : [condition.AND];
    return entries.every(function (entry) {
      return matchesMultipartCondition(entry, state);
    });
  }

  return matchesSimpleCondition(condition, state);
}

function variantKeyMatches(variantKey, state) {
  if (!variantKey) {
    return true;
  }

  const condition = {};
  variantKey.split(",").forEach(function (pair) {
    const separator = pair.indexOf("=");
    if (separator > 0) {
      condition[pair.slice(0, separator)] = pair.slice(separator + 1);
    }
  });

  return matchesSimpleCondition(condition, state);
}

function chooseWeightedApplication(candidate, position, salt) {
  if (!Array.isArray(candidate)) {
    return candidate;
  }

  const weighted = candidate.map(function (entry) {
    return {
      entry,
      weight: Math.max(1, Number(entry.weight) || 1)
    };
  });
  const total = weighted.reduce(function (sum, item) {
    return sum + item.weight;
  }, 0);
  let target =
    stableHash(position.x, position.y, position.z, salt) % total;

  for (const item of weighted) {
    if (target < item.weight) {
      return item.entry;
    }
    target -= item.weight;
  }

  return weighted[0] && weighted[0].entry;
}

function selectApplications(blockStateDefinition, state, position) {
  const selected = [];

  if (blockStateDefinition.variants) {
    let best = null;
    let bestSpecificity = -1;

    Object.entries(blockStateDefinition.variants).forEach(function (
      [variantKey, candidate]
    ) {
      if (!variantKeyMatches(variantKey, state)) {
        return;
      }

      const specificity = variantKey
        ? variantKey.split(",").length
        : 0;
      if (specificity > bestSpecificity) {
        best = candidate;
        bestSpecificity = specificity;
      }
    });

    if (best) {
      selected.push(
        chooseWeightedApplication(best, position, bestSpecificity + 17)
      );
    }
  }

  if (Array.isArray(blockStateDefinition.multipart)) {
    blockStateDefinition.multipart.forEach(function (part, index) {
      if (!matchesMultipartCondition(part.when, state)) {
        return;
      }
      selected.push(
        chooseWeightedApplication(part.apply, position, index + 101)
      );
    });
  }

  return selected.filter(Boolean);
}

function resolveTextureVariable(reference, textures) {
  let current = reference;
  const visited = new Set();

  while (
    typeof current === "string" &&
    current.startsWith("#") &&
    !visited.has(current)
  ) {
    visited.add(current);
    current = textures[current.slice(1)];
  }

  if (!current || String(current).startsWith("#")) {
    return null;
  }

  return normalizeResourceLocation(current, "minecraft");
}

function defaultFaceUv(faceName, from, to) {
  if (faceName === "down") {
    return [from[0], 16 - to[2], to[0], 16 - from[2]];
  }
  if (faceName === "up") {
    return [from[0], from[2], to[0], to[2]];
  }
  if (faceName === "north") {
    return [16 - to[0], 16 - to[1], 16 - from[0], 16 - from[1]];
  }
  if (faceName === "south") {
    return [from[0], 16 - to[1], to[0], 16 - from[1]];
  }
  if (faceName === "west") {
    return [from[2], 16 - to[1], to[2], 16 - from[1]];
  }
  return [16 - to[2], 16 - to[1], 16 - from[2], 16 - from[1]];
}

function faceCorners(faceName, from, to) {
  const x1 = from[0] / 16 - 0.5;
  const y1 = from[1] / 16 - 0.5;
  const z1 = from[2] / 16 - 0.5;
  const x2 = to[0] / 16 - 0.5;
  const y2 = to[1] / 16 - 0.5;
  const z2 = to[2] / 16 - 0.5;

  if (faceName === "north") {
    return [
      new THREE.Vector3(x2, y1, z1),
      new THREE.Vector3(x1, y1, z1),
      new THREE.Vector3(x1, y2, z1),
      new THREE.Vector3(x2, y2, z1)
    ];
  }
  if (faceName === "south") {
    return [
      new THREE.Vector3(x1, y1, z2),
      new THREE.Vector3(x2, y1, z2),
      new THREE.Vector3(x2, y2, z2),
      new THREE.Vector3(x1, y2, z2)
    ];
  }
  if (faceName === "west") {
    return [
      new THREE.Vector3(x1, y1, z1),
      new THREE.Vector3(x1, y1, z2),
      new THREE.Vector3(x1, y2, z2),
      new THREE.Vector3(x1, y2, z1)
    ];
  }
  if (faceName === "east") {
    return [
      new THREE.Vector3(x2, y1, z2),
      new THREE.Vector3(x2, y1, z1),
      new THREE.Vector3(x2, y2, z1),
      new THREE.Vector3(x2, y2, z2)
    ];
  }
  if (faceName === "up") {
    return [
      new THREE.Vector3(x1, y2, z2),
      new THREE.Vector3(x2, y2, z2),
      new THREE.Vector3(x2, y2, z1),
      new THREE.Vector3(x1, y2, z1)
    ];
  }
  return [
    new THREE.Vector3(x1, y1, z1),
    new THREE.Vector3(x2, y1, z1),
    new THREE.Vector3(x2, y1, z2),
    new THREE.Vector3(x1, y1, z2)
  ];
}

function rotateUvs(uvs, rotationDegrees) {
  const turns =
    ((Math.round(rotationDegrees / 90) % 4) + 4) % 4;
  if (turns === 0) {
    return uvs;
  }

  const rotated = uvs.slice();
  for (let turn = 0; turn < turns; turn += 1) {
    rotated.unshift(rotated.pop());
  }
  return rotated;
}

function applyElementRotation(vector, elementRotation) {
  if (!elementRotation || !elementRotation.axis) {
    return vector;
  }

  const originRaw = elementRotation.origin || [8, 8, 8];
  const origin = new THREE.Vector3(
    originRaw[0] / 16 - 0.5,
    originRaw[1] / 16 - 0.5,
    originRaw[2] / 16 - 0.5
  );
  const angle = THREE.MathUtils.degToRad(
    -(Number(elementRotation.angle) || 0)
  );
  const axis = new THREE.Vector3(
    elementRotation.axis === "x" ? 1 : 0,
    elementRotation.axis === "y" ? 1 : 0,
    elementRotation.axis === "z" ? 1 : 0
  );

  const local = vector.clone().sub(origin);

  if (elementRotation.rescale && Math.abs(Math.cos(angle)) > 0.0001) {
    const scale = 1 / Math.cos(angle);
    if (elementRotation.axis === "x") {
      local.y *= scale;
      local.z *= scale;
    } else if (elementRotation.axis === "y") {
      local.x *= scale;
      local.z *= scale;
    } else {
      local.x *= scale;
      local.y *= scale;
    }
  }

  local.applyAxisAngle(axis, angle);
  return local.add(origin);
}

function applyBlockStateRotation(vector, application) {
  const result = vector.clone();
  const rotationX = THREE.MathUtils.degToRad(
    -(Number(application.x) || 0)
  );
  const rotationY = THREE.MathUtils.degToRad(
    -(Number(application.y) || 0)
  );

  if (rotationX) {
    result.applyAxisAngle(new THREE.Vector3(1, 0, 0), rotationX);
  }
  if (rotationY) {
    result.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotationY);
  }

  return result;
}

function getTintColor(blockId, tintIndex) {
  if (tintIndex === undefined || tintIndex === null || tintIndex < 0) {
    return "#ffffff";
  }

  if (blockId === "grass_block") {
    return "#7fbd62";
  }
  if (blockId.includes("leaves")) {
    return "#58a83d";
  }
  if (blockId === "water") {
    return "#4b83d8";
  }
  return "#ffffff";
}

function materialSettings(blockId, tintIndex) {
  const definition = getBlockDefinition(blockId);
  return {
    color: getTintColor(blockId, tintIndex),
    fallbackColor: definition.color || "#ffffff",
    transparent: Boolean(definition.transparent),
    opacity:
      definition.opacity === undefined ? 1 : definition.opacity,
    alphaTest:
      definition.alphaTest === undefined ? 0.08 : definition.alphaTest,
    roughness: definition.pattern === "metal" ? 0.52 : 0.9,
    metalness: definition.pattern === "metal" ? 0.22 : 0.02,
    emissive: definition.emissive || "#000000",
    emissiveIntensity: definition.emissiveIntensity || 0
  };
}

export class MinecraftVanillaModelRenderer {
  constructor(assetLoader) {
    this.assetLoader = assetLoader;
    this.blockStateCache = new Map();
    this.resolvedModelCache = new Map();
    this.renderBundleCache = new Map();
    this.materialCache = new Map();
    this.specialBlockRenderer =
      new MinecraftSpecialBlockRenderer(assetLoader);
    this.blockEntityPreviewRenderer =
      new MinecraftBlockEntityPreviewRenderer(assetLoader);
  }

  async getBlockStateDefinition(blockId) {
    if (!this.blockStateCache.has(blockId)) {
      this.blockStateCache.set(
        blockId,
        Promise.resolve(this.assetLoader.loadBlockState(blockId))
      );
    }
    return this.blockStateCache.get(blockId);
  }

  async resolveModel(resourceLocation, inheritanceStack) {
    const normalized = normalizeResourceLocation(
      resourceLocation,
      "minecraft"
    );

    if (this.resolvedModelCache.has(normalized)) {
      return this.resolvedModelCache.get(normalized);
    }

    const promise = (async () => {
      const stack = inheritanceStack || [];
      if (stack.includes(normalized)) {
        throw new Error(
          "Circular Minecraft model parent chain: " +
            stack.concat(normalized).join(" -> ")
        );
      }

      const raw = await this.assetLoader.loadBlockModel(normalized);
      let parent = null;

      if (
        raw.parent &&
        raw.parent !== "builtin/generated" &&
        raw.parent !== "builtin/entity"
      ) {
        parent = await this.resolveModel(
          raw.parent,
          stack.concat(normalized)
        );
      }

      return {
        resourceLocation: normalized,
        ambientocclusion:
          raw.ambientocclusion !== undefined
            ? raw.ambientocclusion
            : (parent ? parent.ambientocclusion : true),
        textures: {
          ...(parent ? parent.textures : {}),
          ...(raw.textures || {})
        },
        elements:
          raw.elements !== undefined
            ? raw.elements
            : (parent ? parent.elements : []),
        gui_light: raw.gui_light || (parent && parent.gui_light) || null
      };
    })();

    this.resolvedModelCache.set(normalized, promise);
    return promise;
  }

  getMaterial(textureLocation, blockId, tintIndex) {
    const settings = materialSettings(blockId, tintIndex);
    const key = [
      textureLocation || "missing",
      blockId,
      String(tintIndex ?? -1),
      settings.transparent ? "t" : "o",
      settings.opacity
    ].join("|");

    if (this.materialCache.has(key)) {
      return this.materialCache.get(key);
    }

    const material = new THREE.MeshStandardMaterial({
      color: settings.fallbackColor,
      roughness: settings.roughness,
      metalness: settings.metalness,
      transparent: settings.transparent,
      opacity: settings.opacity,
      alphaTest: settings.alphaTest,
      side: settings.transparent ? THREE.DoubleSide : THREE.FrontSide,
      depthWrite:
        !settings.transparent || settings.opacity >= 1,
      emissive: settings.emissive,
      emissiveIntensity: settings.emissiveIntensity
    });

    if (textureLocation) {
      const texturePath = textureLocation
        .replace(/^minecraft:/, "");
      this.assetLoader
        .loadTexture(texturePath)
        .then(function (texture) {
          material.map = texture;
          material.color.set(settings.color);
          material.needsUpdate = true;
        })
        .catch(function (error) {
          console.warn(
            "Unable to load vanilla model texture " +
              textureLocation +
              ".",
            error
          );
        });
    }

    this.materialCache.set(key, material);
    return material;
  }

  async buildRenderBundle(blockId, application) {
    const signature = [
      blockId,
      application.model,
      Number(application.x) || 0,
      Number(application.y) || 0,
      application.uvlock ? 1 : 0
    ].join("|");

    if (this.renderBundleCache.has(signature)) {
      return this.renderBundleCache.get(signature);
    }

    const promise = (async () => {
      const model = await this.resolveModel(application.model);
      const positions = [];
      const uvs = [];
      const materialList = [];
      const materialIndexByKey = new Map();
      const groups = [];

      function getLocalMaterialIndex(material, key) {
        if (materialIndexByKey.has(key)) {
          return materialIndexByKey.get(key);
        }
        const index = materialList.length;
        materialList.push(material);
        materialIndexByKey.set(key, index);
        return index;
      }

      (model.elements || []).forEach((element) => {
        const from = element.from || [0, 0, 0];
        const to = element.to || [16, 16, 16];

        FACE_NAMES.forEach((faceName) => {
          const face = element.faces && element.faces[faceName];
          if (!face) {
            return;
          }

          const textureLocation = resolveTextureVariable(
            face.texture,
            model.textures
          );
          const tintIndex =
            face.tintindex === undefined ? -1 : Number(face.tintindex);
          const material = this.getMaterial(
            textureLocation,
            blockId,
            tintIndex
          );
          const materialKey = [
            textureLocation || "missing",
            blockId,
            tintIndex
          ].join("|");
          const materialIndex = getLocalMaterialIndex(
            material,
            materialKey
          );

          const rawCorners = faceCorners(faceName, from, to);
          const transformedCorners = rawCorners.map((corner) => {
            const elementRotated = applyElementRotation(
              corner,
              element.rotation
            );
            return applyBlockStateRotation(
              elementRotated,
              application
            );
          });

          const uvRect = face.uv || defaultFaceUv(faceName, from, to);
          const baseUvs = [
            [uvRect[0] / 16, 1 - uvRect[3] / 16],
            [uvRect[2] / 16, 1 - uvRect[3] / 16],
            [uvRect[2] / 16, 1 - uvRect[1] / 16],
            [uvRect[0] / 16, 1 - uvRect[1] / 16]
          ];

          let uvRotation = Number(face.rotation) || 0;
          if (application.uvlock) {
            uvRotation -= Number(application.y) || 0;
          }
          const rotatedUvs = rotateUvs(baseUvs, uvRotation);

          const triangleOrder = [0, 1, 2, 0, 2, 3];
          const groupStart = positions.length / 3;

          triangleOrder.forEach(function (cornerIndex) {
            const corner = transformedCorners[cornerIndex];
            const uv = rotatedUvs[cornerIndex];
            positions.push(corner.x, corner.y, corner.z);
            uvs.push(uv[0], uv[1]);
          });

          groups.push({
            start: groupStart,
            count: 6,
            materialIndex
          });
        });
      });

      if (positions.length === 0) {
        const particleReference =
          model.textures.particle ||
          Object.values(model.textures || {})[0];
        const particleTexture = resolveTextureVariable(
          particleReference,
          model.textures
        );

        if (particleTexture) {
          return {
            signature: signature + "|particle-fallback",
            geometry: new THREE.BoxGeometry(0.98, 0.98, 0.98),
            materials: this.getMaterial(
              particleTexture,
              blockId,
              -1
            ),
            model: application.model,
            approximate: true
          };
        }

        throw new Error(
          "Minecraft model has no renderable elements: " +
            application.model
        );
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3)
      );
      geometry.setAttribute(
        "uv",
        new THREE.Float32BufferAttribute(uvs, 2)
      );
      geometry.computeVertexNormals();
      groups.forEach(function (group) {
        geometry.addGroup(
          group.start,
          group.count,
          group.materialIndex
        );
      });
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      return {
        signature,
        geometry,
        materials: materialList,
        model: application.model
      };
    })();

    this.renderBundleCache.set(signature, promise);
    return promise;
  }

  async createRenderParts(blockEntry) {
    const mergedState = {
      ...getDefaultBlockState(blockEntry.blockId),
      ...(blockEntry.state || {})
    };
    const renderEntry = {
      ...blockEntry,
      state: mergedState
    };

    if (
      this.blockEntityPreviewRenderer.shouldReplaceBase(
        renderEntry
      )
    ) {
      return this.blockEntityPreviewRenderer.createReplacementParts(
        renderEntry
      );
    }

    const overlays =
      this.blockEntityPreviewRenderer.createOverlayParts(
        renderEntry
      );

    if (
      this.specialBlockRenderer.isAlwaysSpecial(
        blockEntry.blockId
      )
    ) {
      try {
        const specialParts =
          await this.specialBlockRenderer.createRenderParts(
            renderEntry
          );
        return specialParts.concat(overlays);
      } catch (error) {
        console.warn(
          "Special renderer failed for " +
            blockEntry.blockId +
            "; trying vanilla model.",
          error
        );
      }
    }

    const definition = await this.getBlockStateDefinition(
      blockEntry.blockId
    );
    const applications = selectApplications(
      definition,
      mergedState,
      blockEntry
    );

    const parts = [];
    for (const application of applications) {
      if (!application || !application.model) {
        continue;
      }
      parts.push(
        await this.buildRenderBundle(
          blockEntry.blockId,
          application
        )
      );
    }

    const onlyApproximate =
      parts.length > 0 &&
      parts.every((part) => Boolean(part.approximate));

    if (
      this.specialBlockRenderer.canRender(blockEntry.blockId) &&
      (parts.length === 0 || onlyApproximate)
    ) {
      try {
        const legacyParts =
          await this.specialBlockRenderer.createRenderParts(
            renderEntry
          );
        return legacyParts.concat(overlays);
      } catch (error) {
        console.warn(
          "Legacy special renderer failed for " +
            blockEntry.blockId +
            "; keeping vanilla fallback.",
          error
        );
      }
    }

    return parts.concat(overlays);
  }

  dispose() {
    this.renderBundleCache.forEach(function (promise) {
      Promise.resolve(promise).then(function (bundle) {
        bundle.geometry.dispose();
      }).catch(function () {});
    });

    this.materialCache.forEach(function (material) {
      material.dispose();
    });
    this.specialBlockRenderer.dispose();
    this.blockEntityPreviewRenderer.dispose();
  }
}
